import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import NetworkGraphV2 from "./NetworkGraphV2";
import isEqual from "react-fast-compare";
import NetworkClusterV2 from "./NetworkClusterV2";
import { globalChartColor } from "../../../../../redux/constants/enums";
import {
  EDGES_BASE_LENGTH,
  EDGES_ROUND_VALUE,
  MAX_EDGES_ROUND_VALUE,
  MAX_NODE_SIZE,
  MAX_WIDTH_OF_EDGE,
  MIN_EDGES_ROUND_VALUE,
  MIN_NODE_SIZE,
  MIN_WIDTH_OF_EDGE,
  SCALE_FACTOR_EDGES_LENGTH,
} from "../../../../../redux/constants/networkEnums";
import { newColorShadeInHex6 } from "../../../../../redux/constants";

const MultiNetowrkGraphUsingClusteringV2 = ({
  metadata,
  single,
  networkLinkLength,
  style,
  id,
  chartGlobalDataLabel,
  chartGradientColor,
  chartMarkers,
  widget_index,
  section_index,
  PdfDownloadStatus,
  template_id,
  type,
  isNetworkDeepDive,
  hideSetting,
  onSettingChange,
  dashboard,
}) => {
  const authParams = useSelector((state) => state?.Authentication?.authParams);

  const [networkClusterConfig, setNetworkClusterConfig] = useState(null);
  const [clustersData, setClustersData] = useState([]);
  const [dataProcessing, setDataProcessing] = useState(true);

  // Sample function to calculate Euclidean distance
  const distance = (nodeA, nodeB) => {
    return Math.sqrt(
      Math.pow(nodeA?.x - nodeB?.x, 2) + Math.pow(nodeA?.y - nodeB?.y, 2)
    );
  };
  const generateRandomCoordinates = (nodes, width, height) => {
    if (nodes && !Array.isArray(nodes)) return [];
    return nodes?.map((node) => ({
      ...node,
      x: Math.random() * width,
      y: Math.random() * height,
    }));
  };

  // Clustering nodes
  function clusterNodes(nodes, threshold) {
    const clusters = [];
    const visited = new Set();

    nodes.forEach((node, index) => {
      if (visited.has(node?.id)) return;

      const cluster = {
        id: `cluster_${index}`,
        nodes: [node],
        x: node?.x,
        y: node?.y,
      };
      visited.add(node?.id);

      nodes.forEach((otherNode) => {
        if (
          !visited.has(otherNode?.id) &&
          distance(node, otherNode) < threshold
        ) {
          cluster?.nodes?.push(otherNode);
          visited.add(otherNode?.id);
        }
      });

      clusters.push(cluster);
    });

    return clusters;
  }
  // Function to determine node size based on degree
  const getNodeSize = (degree) => (degree < 3 ? 20 : degree * 5);

  // Function to determine edge length based on the degrees of connected nodes
  const getEdgeLength = (degree = 0, fromDegree = 0, toDegree = 0) => {
    const baseLength = EDGES_BASE_LENGTH; // Base edge length
    const scaleFactor = SCALE_FACTOR_EDGES_LENGTH; // Scale factor for length adjustment
    if (degree > 0) {
      return baseLength + degree * scaleFactor;
    } else {
      return baseLength + (fromDegree + toDegree) * scaleFactor;
    }
  };
  // Utility function to calculate scaled value
  const getScaledValueOfRoundness = (count, minCount, maxCount) => {
    try {
      // Ensure minCount is less than maxCount to avoid division by zero
      if (minCount >= maxCount) {
        throw new Error("minCount should be less than maxCount");
      }

      // Calculate the scaled value
      const scaledValue =
        MIN_EDGES_ROUND_VALUE +
        ((count - minCount) / (maxCount - minCount)) *
          (MAX_EDGES_ROUND_VALUE - MIN_EDGES_ROUND_VALUE);

      // Return the scaled value, ensuring it's between 0.2 and 1
      return Math.max(
        MIN_EDGES_ROUND_VALUE,
        Math.min(scaledValue, MAX_EDGES_ROUND_VALUE)
      );
    } catch (error) {
      console.log("Error : ", error);
    }
    return count;
  };
  const getSingleNetworkClusterData = (
    clusters,
    numOfNetwork,
    cluster_id,
    graphID,
    offset_index,
    nodeDegreeMap
  ) => {
    let singleCluster = {
      id: "",
      edges: [],
    };
    // // Node degree map to store the count of edges for each node
    // const nodeDegreeMap = {};

    // Calculate the degree of each node
    if (
      clusters?.data &&
      Array.isArray(clusters?.data) &&
      clusters?.data?.length > 0 &&
      typeof cluster_id === "string" &&
      cluster_id?.includes("common_cluster")
    ) {
      let idForCluster =
        (graphID ? graphID : "network_graph") + "$N$" + cluster_id;
      clusters.data.forEach((edge) => {
        let updatedFromIdForCommonCluster = null;
        if (idForCluster?.includes("common_cluster") && edge?.FromClusterId) {
          let updatedClusterId =
            (graphID ? graphID : "network_graph") + "$N$" + edge?.FromClusterId;

          updatedFromIdForCommonCluster = updatedClusterId + "$N$" + edge?.from;
        }
        let fromId = updatedFromIdForCommonCluster
          ? updatedFromIdForCommonCluster
          : idForCluster + "$N$" + edge?.from;
        let toId = idForCluster + "$N$" + edge?.to;
        if (!nodeDegreeMap[fromId]) nodeDegreeMap[fromId] = 0;
        if (!nodeDegreeMap[toId]) nodeDegreeMap[toId] = 0;
        nodeDegreeMap[fromId]++;
        nodeDegreeMap[toId]++;
      });
    }
    // console.log(
    //   "nodeDegreeMap : ",
    //   nodeDegreeMap,
    //   "cluster_id : ",
    //   cluster_id,
    //   Object?.keys(nodeDegreeMap)?.length,
    //   Object.keys(clusters?.nodes)?.length
    // );

    let totalNumberOfInfluencerAuthor = 0;
    let totalNumberOfNodes = 0;
    if (
      (clusters?.data &&
        Array.isArray(clusters?.data) &&
        clusters?.data?.length > 0) ||
      (clusters?.nodes &&
        typeof clusters?.nodes === "object" &&
        Object.keys(clusters?.nodes)?.length > 0)
    ) {
      singleCluster.id =
        (graphID ? graphID : "network_graph") + "$N$" + cluster_id;
      singleCluster.name =
        (graphID ? graphID : "network_graph") + "$N$" + cluster_id;

      const colorArray = globalChartColor;
      let j = offset_index ? offset_index : 0;
      let noOfIterationInColors = 0;
      let lengthOfColors = colorArray?.length;

      singleCluster.nodes = Object.keys(clusters?.nodes).map(function (
        id,
        index
      ) {
        let dataColor;
        let node = clusters?.nodes?.[id];
        if (!node?.isretweetauthor) totalNumberOfInfluencerAuthor++;
        totalNumberOfNodes++;
        if (
          node?.color !== undefined &&
          node?.color !== "#000000" &&
          node?.color !== null
        ) {
          dataColor = node?.color;
        } else if (
          node?.link_color !== undefined &&
          node?.link_color !== "#000000" &&
          node?.link_color !== null &&
          node?.link_color
        ) {
          dataColor = node?.link_color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            dataColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    globalChartColor[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : globalChartColor[j];
          } else {
            dataColor = globalChartColor[j];
          }
          j++;
        }
        let lighterColor = newColorShadeInHex6(dataColor, 60);
        let darkerColor = dataColor;
        // here calculate darker text color for showing in tooltip
        let darkerTextColor = newColorShadeInHex6(dataColor, -80);
        if (
          node?.link_color !== undefined &&
          node?.link_color !== "#000000" &&
          node?.link_color !== null &&
          !singleCluster?.link
        ) {
          singleCluster.link = {
            color: node?.link_color,
          };
        } else if (!singleCluster?.link) {
          singleCluster.link = {
            color: dataColor,
          };
        }
        // for tooltip on hover

        let clr = chartGradientColor
          ? {
              radialGradient: {
                cx: 0.5,
                cy: 0.3,
                r: 0.7,
              },
              stops: [
                [0, lighterColor],
                [1, darkerColor], // darken
              ],
            }
          : lighterColor;
        let authorDetails = {
          AuthorName: node?.AuthorName,
          AuthorURL: node?.AuthorURL,
          AuthorScreenName: node?.AuthorScreenName,
          AuthorIsVerified: node?.AuthorIsVerified,
          AuthorBio: node?.AuthorBio,
          ChannelGroupId: node?.ChannelGroupID
            ? node?.ChannelGroupID
            : node?.ChannelGroupId,
          CreatedDate: node?.CreatedDate,
          Followers: node?.Followers,
          Following: node?.Following,
          ProfilePicture: node?.ProfilePicture
            ? node?.ProfilePicture
            : node?.ProfilePic,
          UserSentiment: node?.UserSentiment,
          main_authorid: node?.main_authorid,
          u_authorid: node?.u_authorid,
        };
        let postCardDetails =
          !node?.isretweetauthor && isNetworkDeepDive
            ? {
                ...node,
                ChannelGroupId: node?.ChannelGroupID
                  ? node?.ChannelGroupID
                  : node?.ChannelGroupId,
                ProfilePicture: node?.ProfilePicture
                  ? node?.ProfilePicture
                  : node?.ProfilePic,
              }
            : null;
        const degree = nodeDegreeMap?.[id] || 0;
        return {
          id: singleCluster?.id + "$N$" + id,
          category: singleCluster?.id,
          group: singleCluster?.id,
          name: node?.AuthorName
            ? node?.AuthorName
            : node?.node_name
            ? node?.node_name
            : node?.AuthorScreenName,
          label: chartGlobalDataLabel
            ? node?.node_name
              ? node?.node_name
              : node?.AuthorScreenName
            : undefined,
          shape: node?.node_image ? "circularImage" : "dot",
          image: node?.node_image
            ? node?.node_image
            : node?.u_picurl
            ? node?.u_picurl
            : undefined,
          // brokenImage: node?.node_image ? node?.node_image : node?.u_picurl,
          // title: element,
          // marker: {
          //   enabled: true,
          //   radius:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   // symbol: "circle",
          //   // symbol: base64ImageFromUrl ? `url(${base64ImageFromUrl})` : "circle",
          //   symbol: "url(" + el?.u_picurl + ")",
          //   width:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   height:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   lineColor: el?.link_color,
          // },
          nodeDataColor: dataColor,
          color: {
            borderWidth: node?.node_image ? 0 : 2,
            borderWidthSelected: 3,
            border: darkerColor,
            background: chartGradientColor
              ? {
                  radialGradient: {
                    cx: 0.5,
                    cy: 0.3,
                    r: 0.7,
                  },
                  stops: [
                    [0, lighterColor],
                    [1, darkerColor], // darken
                  ],
                }
              : lighterColor,
          },
          font: {
            color: darkerColor,
            align: "center",
          },
          // size: getNodeSize(degree),
          size: node?.node_image
            ? node?.radius > MAX_NODE_SIZE
              ? MAX_NODE_SIZE
              : node?.radius < MIN_NODE_SIZE
              ? MIN_NODE_SIZE
              : parseInt(node?.radius)
            : MIN_NODE_SIZE,
          authorDetails: authorDetails,
          postCardDetails: postCardDetails,
        };
      });
      // let rawNodes = Array.isArray(singleCluster?.nodes)
      //   ? singleCluster?.nodes
      //   : [];
      // // Generate coordinates if not present
      // if (
      //   !rawNodes?.some(
      //     (node) => node?.x !== undefined && node?.y !== undefined
      //   )
      // ) {
      //   rawNodes = generateRandomCoordinates(rawNodes, 1000, 800); // Adjust width and height as needed
      // }

      // console.log("rawNodes : ", rawNodes);
      // if (rawNodes?.length > 0) {
      //   singleCluster.nodes = rawNodes;
      // }
      let k = j;
      let updatedEdgesData = [];
      clusters?.data &&
        Array.isArray(clusters?.data) &&
        clusters?.data?.length > 0 &&
        clusters?.data?.forEach((d, i) => {
          let dataLinkColor;
          if (
            d?.link_color !== undefined &&
            d?.link_color !== "#000000" &&
            d?.link_color !== null &&
            d?.link_color
          ) {
            dataLinkColor = d?.link_color;
          } else {
            if (k >= lengthOfColors) {
              noOfIterationInColors = parseInt(k / lengthOfColors);
              let rem = parseInt(k % lengthOfColors);
              dataLinkColor =
                noOfIterationInColors > 0
                  ? newColorShadeInHex6(
                      globalChartColor[rem],
                      parseInt(noOfIterationInColors * 5)
                    )
                  : globalChartColor[j];
            } else {
              dataLinkColor = globalChartColor[k];
            }
            k++;
          }
          // let lighterColor = newColorShadeInHex6(dataLinkColor, 60);
          // let darkerColor = dataLinkColor;
          // here calculate darker text color for showing in tooltip
          let darkerTextColor = newColorShadeInHex6(dataLinkColor, -80);
          let updatedFromIdForCommonCluster = null;
          if (
            singleCluster?.id?.includes("common_cluster") &&
            d?.FromClusterId
          ) {
            let updatedClusterId =
              (graphID ? graphID : "network_graph") + "$N$" + d?.FromClusterId;

            updatedFromIdForCommonCluster = updatedClusterId + "$N$" + d?.from;
          }

          const fromDegree =
            nodeDegreeMap[
              updatedFromIdForCommonCluster
                ? updatedFromIdForCommonCluster
                : singleCluster?.id + "$N$" + d?.from
            ] || 0;
          const toDegree =
            nodeDegreeMap[singleCluster?.id + "$N$" + d?.to] || 0;

          // console.log(
          //   "for node degree d : ",
          //   d,
          //   "from frontend : ",
          //   fromDegree + toDegree,
          //   "getEdgeLength(d?.degree) : ",
          //   getEdgeLength(d?.degree),
          //   "getEdgeLength(0, fromDegree, toDegree) : ",
          //   getEdgeLength(0, fromDegree, toDegree)
          // );
          let edgesUniqueId =
            (updatedFromIdForCommonCluster
              ? updatedFromIdForCommonCluster + "$N$" + d?.to
              : singleCluster?.id + "$N$" + d?.from + "$N$" + d?.to) +
            "_edges_" +
            i;
          let edgesObj = {
            id: edgesUniqueId,
            level: updatedFromIdForCommonCluster ? 2 : 1,
            from: updatedFromIdForCommonCluster
              ? updatedFromIdForCommonCluster
              : singleCluster?.id + "$N$" + d?.from,
            to: singleCluster?.id + "$N$" + d?.to,
            edgeColor: dataLinkColor
              ? dataLinkColor
              : singleCluster?.link?.color
              ? singleCluster?.link?.color
              : d?.link_color
              ? d?.link_color
              : null,
            color: {
              inherit: false,
              color: dataLinkColor
                ? dataLinkColor
                : singleCluster?.link?.color
                ? singleCluster?.link?.color
                : d?.link_color
                ? d?.link_color
                : null,
              highlight: darkerTextColor,
              hover: darkerTextColor,
            },
            // length: updatedFromIdForCommonCluster
            //   ? 20000
            //   : d?.link_length
            //   ? d?.link_length
            //   : 200,
            // length: getEdgeLength(fromDegree, toDegree),
            length: updatedFromIdForCommonCluster
              ? d?.degree > 0
                ? getEdgeLength(d?.degree)
                : getEdgeLength(0, fromDegree, toDegree)
              : d?.link_length
              ? d?.link_length
              : EDGES_BASE_LENGTH, // Set edge length based on degrees
            numberOfLines: d?.number_of_lines,
            width: MIN_WIDTH_OF_EDGE,
          };
          updatedEdgesData?.push(edgesObj);
          if (d?.number_of_lines > 1) {
            for (let st_i = 1; st_i < d?.number_of_lines; st_i++) {
              // Get the scaled value for the given count
              const scaledValue = getScaledValueOfRoundness(st_i, 5, 20);
              updatedEdgesData?.push({
                ...edgesObj,
                id: edgesUniqueId + "_mtline_" + st_i,
                dashes: true,
                smooth: {
                  // type: "curvedCCW", // Curves the lines counter-clockwise
                  type: "curvedCW", // Curves the lines counter-clockwise
                  // type: "cubicBezier",
                  roundness: scaledValue, // Adjust the roundness to separate edges
                },
                width: MIN_WIDTH_OF_EDGE,
              });
            }
          }

          // return {
          //   level: updatedFromIdForCommonCluster ? 2 : 1,
          //   from: updatedFromIdForCommonCluster
          //     ? updatedFromIdForCommonCluster
          //     : singleCluster?.id + "$N$" + d?.from,
          //   to: singleCluster?.id + "$N$" + d?.to,
          //   color: {
          //     inherit: false,
          //     color: updatedFromIdForCommonCluster
          //       ? "#FFDB4D"
          //       : dataLinkColor
          //       ? dataLinkColor
          //       : singleCluster?.link?.color
          //       ? singleCluster?.link?.color
          //       : d?.link_color
          //       ? d?.link_color
          //       : null,
          //     highlight: "#FFDB4D" ? "#FFDB4D" : darkerTextColor,
          //     hover: "#FFDB4D" ? "#FFDB4D" : darkerTextColor,
          //   },
          //   // length: updatedFromIdForCommonCluster
          //   //   ? 20000
          //   //   : d?.link_length
          //   //   ? d?.link_length
          //   //   : 200,
          //   // length: getEdgeLength(fromDegree, toDegree),
          //   length: updatedFromIdForCommonCluster
          //     ? d?.degree > 0
          //       ? getEdgeLength(d?.degree)
          //       : getEdgeLength(0, fromDegree, toDegree)
          //     : d?.link_length
          //     ? d?.link_length
          //     : 200, // Set edge length based on degrees
          // };
        });
      singleCluster.edges =
        updatedEdgesData?.length > 0 ? updatedEdgesData : [];
    }
    // console.log(
    //   "totalNumberOfNodes : ",
    //   totalNumberOfNodes,
    //   "totalNumberOfInfluencerAuthor : ",
    //   totalNumberOfInfluencerAuthor,
    //   "totalNumberOfRetweetAuthor : ",
    //   totalNumberOfNodes - totalNumberOfInfluencerAuthor
    // );
    singleCluster.nodeLength = singleCluster?.nodes?.length || 0;
    singleCluster.edgesLinkLength = singleCluster?.edges?.length || 0;
    return singleCluster;
  };

  // This is used for creating whole network data of Network Graph Charts
  useEffect(() => {
    let updatedNodes = [];
    let updatedEdges = [];
    let updatedClustersData = [];
    // Node degree map to store the count of edges for common links
    const nodeDegreeMap = {};
    if (single && Array.isArray(single)) {
      let offset_starting_index = 0;
      single?.forEach((cluster, cluster_index) => {
        offset_starting_index =
          cluster_index > 0
            ? offset_starting_index +
              (cluster?.nodes && Object.keys(cluster?.nodes)?.length)
            : 0;
        let singleClusterData = getSingleNetworkClusterData(
          cluster,
          single?.length,
          cluster?.cluster_id ? cluster?.cluster_id : cluster_index,
          id,
          offset_starting_index,
          nodeDegreeMap
        );
        if (singleClusterData?.id?.includes("common_cluster")) {
          let clusterData = {
            ...singleClusterData,
          };
          updatedClustersData?.unshift(clusterData);
        } else {
          let clusterData = {
            ...singleClusterData,
          };
          updatedClustersData?.push(clusterData);
        }
        if (
          singleClusterData?.edges &&
          Array.isArray(singleClusterData?.edges) &&
          singleClusterData?.edges?.length > 0
        ) {
          updatedEdges = [...updatedEdges, ...singleClusterData?.edges];
        }
        if (
          singleClusterData?.nodes &&
          Array.isArray(singleClusterData?.nodes) &&
          singleClusterData?.nodes?.length > 0
        ) {
          updatedNodes = [...updatedNodes, ...singleClusterData?.nodes];
        }
      });
    } else if (single && single?.data) {
      let singleClusterData = getSingleNetworkClusterData(
        single,
        1,
        0,
        id,
        0,
        nodeDegreeMap
      );
      if (singleClusterData?.id?.includes("common_cluster")) {
        updatedClustersData?.unshift({
          ...singleClusterData,
        });
      } else {
        updatedClustersData?.push({
          ...singleClusterData,
        });
      }
      if (
        singleClusterData?.edges &&
        Array.isArray(singleClusterData?.edges) &&
        singleClusterData?.edges?.length > 0
      ) {
        updatedEdges = [...updatedEdges, ...singleClusterData?.edges];
      }
      if (
        singleClusterData?.nodes &&
        Array.isArray(singleClusterData?.nodes) &&
        singleClusterData?.nodes?.length > 0
      ) {
        updatedNodes = [...updatedNodes, ...singleClusterData?.nodes];
      }
    }
    if (updatedNodes?.length > 0 && updatedEdges?.length > 0) {
      setNetworkClusterConfig({
        nodes: updatedNodes,
        edges: updatedEdges,
      });
    }
    // console.log("updatedClustersData : ", updatedClustersData);
    if (updatedClustersData?.length > 0) {
      setClustersData([...updatedClustersData]);
    }
  }, [single, chartGlobalDataLabel]);

  // This is used, whenever we update networkClusterConfig need to console
  // useEffect(() => {
  //   // console.log(
  //   //   "useEffect got called : networkClusterConfig : ",
  //   //   networkClusterConfig
  //   // );
  // }, [networkClusterConfig]);

  const options3 = {
    autoResize: true,
    height: "100%",
    width: "100%",
    locale: "en",

    layout: {
      improvedLayout: false, // Enable improved layout algorithm
      randomSeed: 2,
      clusterThreshold: 150,
    },
    manipulation: {
      enabled: false,
    },
    nodes: {
      shape: "dot",
      size: 20,
      borderWidth: 2,
      borderWidthSelected: 3,
      // scaling: {
      //   min: 10,
      //   max: 30,
      // },
      mass: 2,
      color: {
        border: "#000000",
        background: "#ffffff",
      },
      font: {
        size: 14,
        color: "#000000",
        face: "arial",
      },
      shadow: {
        enabled: true,
      },
    },
    edges: {
      width: 2,
      color: { inherit: true },
      physics: true,
      // smooth: {
      //   type: "cubicBezier",
      //   forceDirection: "horizontal",
      //   roundness: 0,
      // },
      // smooth: {
      //   enabled: true,
      //   type: "continuous",
      //   forceDirection: "horizontal",
      //   roundness: 0,
      // },
      smooth: {
        enabled: true,
        type: "curvedCCW",
        forceDirection: "horizontal",
        roundness: 0,
      },
      scaling: {
        min: MIN_WIDTH_OF_EDGE,
        max: MAX_WIDTH_OF_EDGE,
        label: {
          enabled: true,
          min: 5,
          max: 12,
          maxVisible: 15,
          drawThreshold: 5,
        },
      },
      font: {
        size: 10,
        color: "#000000",
        strokeWidth: 0,
        align: "middle",
      },
      // arrows: {
      //   to: {
      //     enabled: false,
      //     scaleFactor: 0.5,
      //   },
      // },
    },

    interaction: {
      hover: true,
      zoomView: true,
      navigationButtons: false,
      keyboard: false,
      tooltipDelay: 300,
      hideEdgesOnDrag: false,
      hideEdgesOnZoom: false,
      hideNodesOnDrag: false,
    },
    physics: {
      enabled: true,
      // barnesHut: {
      //   gravitationalConstant: -500000,
      //   centralGravity: 2,
      //   springLength: 230,
      //   springConstant: 0.02,
      //   damping: 0.5,
      //   avoidOverlap: 1,
      // },
      // repulsion: {
      //   centralGravity: 2,
      //   springLength: 230,
      //   springConstant: 0.02,
      //   nodeDistance: 200,
      //   damping: 0.9,
      // },
      barnesHut: {
        gravitationalConstant: -500000,
        centralGravity: 0.02,
        springLength: 230,
        springConstant: 0.02,
        damping: 0.5,
        avoidOverlap: 1,
      },
      solver: "barnesHut",
      timestep: 0.4,
      minVelocity: 10,
      maxVelocity: 100,
      stabilization: true,
      stabilization: {
        enabled: true,
        iterations: 1000, // Adjust iterations as needed
        updateInterval: 25,
        onlyDynamicEdges: false,
        fit: true,
      },
      stabilization: {
        iterations: 1000, // Ensure stabilization is completed
      },
    },

    // old code here
    // animation: true, // default duration is 1000ms and default easingFunction is easeInOutQuad.
    // animation: {
    //   duration: 5000,
    //   easingFunction: "linear",
    //   scale: 2.0,
    // },

    // old code here
    // edges: {
    //   width: 2,
    //   color: { inherit: true },
    //   physics: true,
    //   smooth: {
    //     type: "cubicBezier",
    //     forceDirection: "horizontal",
    //     roundness: 0,
    //   },
    // },
    // nodes: {
    //   shape: "dot",
    //   borderWidth: 2,
    //   borderWidthSelected: 3,
    //   size: 15,
    //   color: {
    //     border: "#222222",
    //     background: "#666666",
    //   },
    //   font: { color: "black" },
    //   shadow: {
    //     enabled: true,
    //   },
    // },

    // option 1 barnesHut
    // physics: {
    //   enabled: true,
    //   barnesHut: {
    //     gravitationalConstant: -500000,
    //     centralGravity: 2,
    //     springLength: 230,
    //     springConstant: 0.02,
    //     damping: 0.5,
    //     avoidOverlap: 1,
    //   },
    //   solver: "barnesHut",
    //   minVelocity: 10,
    //   maxVelocity: 100,
    // },

    // option 2 forceAtlas2Based
    // physics: {
    //   enabled: true,
    //   forceAtlas2Based: {
    //     gravitationalConstant: -1000,
    //     centralGravity: 0.005,
    //     springLength: 30,
    //     springConstant: 0.5,
    //     damping: 0.5,
    //     avoidOverlap: 1,
    //   },
    //   minVelocity: 10,
    //   maxVelocity: 100,
    //   solver: "forceAtlas2Based",
    // },

    // interaction: {
    //   hover: true,
    //   navigationButtons: true,
    //   keyboard: false,
    // },
  };
  const getNetworkOptions = () => {
    return {
      autoResize: true,
      height: "100%",
      width: "100%",
      locale: "en",
      edges: {
        color: "#000000",
        smooth: {
          enabled: true,
          type: "continuous",
        },
        scaling: {
          min: 5,
          max: 8,
          label: {
            enabled: true,
            min: 5,
            max: 12,
            maxVisible: 15,
            drawThreshold: 5,
          },
        },
        font: {
          size: 10,
          color: "#000000",
          strokeWidth: 0,
          align: "middle",
        },
        arrows: {
          to: {
            enabled: false,
            scaleFactor: 0.5,
          },
        },
      },
      nodes: {
        color: {
          border: "#000000",
          background: "#ffffff",
        },
        font: {
          size: 14,
          color: "#000000",
          face: "arial",
        },
        scaling: {
          min: 10,
          max: 30,
        },
        shape: "dot",
        size: 20,
        shadow: true,
        mass: 2,
      },
      interaction: {
        hover: true,
        navigationButtons: true,
        keyboard: false,
        tooltipDelay: 300,
        hideEdgesOnDrag: true,
        hideEdgesOnZoom: true,
        hideNodesOnDrag: true,
      },
      physics: {
        enabled: true,
        stabilization: false,
        barnesHut: {
          gravitationalConstant: -30000,
          centralGravity: 0.3,
          springLength: 200,
          springConstant: 0.05,
          damping: 0.09,
          avoidOverlap: 0.6,
        },
        solver: "barnesHut",
        timestep: 0.5,
        // stabilization: {
        //   enabled: true,
        //   iterations: 1000,
        //   updateInterval: 100,
        //   onlyDynamicEdges: false,
        //   fit: true,
        // },
      },
      layout: {
        improvedLayout: true,
      },
    };
  };
  const options = {
    autoResize: true,
    height: "100%",
    width: "100%",
    locale: "en",
    //working
    // physics: {
    //   enabled: true,
    //   solver: "barnesHut",
    //   barnesHut: {
    //     gravitationalConstant: -500000,
    //     centralGravity: 0.2,
    //     springLength: 300, // Increase spring length for better spacing
    //     springConstant: 0.18,
    //     damping: 0.9,
    //     avoidOverlap: 0.9,
    //   },
    //   timestep: 0.4,
    //   minVelocity: 10,
    //   maxVelocity: 100,
    //   stabilization: true,
    //   stabilization: {
    //     enabled: true,
    //     iterations: 1000, // Adjust iterations as needed
    //     updateInterval: 25,
    //     onlyDynamicEdges: false,
    //     fit: true,
    //   },
    //   stabilization: {
    //     iterations: 1000, // Ensure stabilization is completed
    //   },
    // },

    physics: {
      enabled: true,
      solver: "barnesHut",
      barnesHut: {
        gravitationalConstant:
          clustersData?.length > 5
            ? -1 * (clustersData?.length * 5000)
            : -50000,
        centralGravity: 0.2,
        springLength: EDGES_BASE_LENGTH, // Increase spring length for better spacing
        springConstant: 0.002,
        damping: 0.09,
        avoidOverlap: 0.9,
      },
      timestep: 0.5,
      minVelocity: 10,
      maxVelocity: 100,
      stabilization: true,
      stabilization: {
        enabled: true,
        iterations: 1500, // Increase iterations for better stabilization
        updateInterval: 25,
        onlyDynamicEdges: false,
        fit: true,
      },
      stabilization: {
        iterations: 1500, // Ensure stabilization is completed // Increase iterations for better stabilization
      },
    },
    // physics: {
    //   enabled: true,
    //   solver: "repulsion",
    //   repulsion: {
    //     centralGravity: 0.1,
    //     springLength: 300, // Increase spring length for better spacing
    //     springConstant: 0.05,
    //     nodeDistance: 200, // Increase node distance
    //     damping: 0.09,
    //   },
    //   hierarchicalRepulsion: {
    //     nodeDistance: 200, // Also adjust for hierarchical layouts
    //   },
    //   maxVelocity: 50,
    //   minVelocity: 0.1,
    //   stabilization: {
    //     enabled: true,
    //     iterations: 1000, // Increase iterations for better stabilization
    //     updateInterval: 25,
    //   },
    // },
    edges: {
      physics: true,
      // smooth: {
      //   enabled: true,
      //   type: "cubicBezier",
      //   forceDirection: "none",
      //   roundness: 0.3,
      // },
      // smooth: {
      //   enabled: true,
      //   type: "vertical",
      //   forceDirection: "none",
      //   roundness: 0.3,
      // },
      // smooth: {
      //   enabled: true,
      //   type: "discrete",
      //   forceDirection: "none",
      //   roundness: 0.5,
      // },
      smooth: {
        enabled: true,
        type: "continuous",
        forceDirection: "none",
        roundness: EDGES_ROUND_VALUE,
      },
      hidden: false,
      // smooth: {
      //   enabled: true,
      //   type: "curvedCCW",
      //   forceDirection: "horizontal",
      //   roundness: 0.5,
      // },
      color: { inherit: false },

      width: MIN_WIDTH_OF_EDGE,
      selectionWidth: 10,
      hoverWidth: function (width) {
        return width + 10;
      },
      // length: 20000, // Increase the length to prevent collapsing
      // arrows: {
      //   from: { enabled: false, scaleFactor: 0.5 },
      //   to: { enabled: false, scaleFactor: 0.5 },
      // },
      scaling: {
        min: MIN_WIDTH_OF_EDGE,
        max: MAX_WIDTH_OF_EDGE,
        label: {
          enabled: true,
          min: 20,
          max: 50,
          maxVisible: 50,
          drawThreshold: 5,
        },
      },
      font: {
        // size: 45,
        // color: "#fffff",
        // strokeWidth: 0,
        align: "middle",
      },
    },
    nodes: {
      shape: "dot",
      size: 20,
      borderWidth: 2,
      borderWidthSelected: 3,
      scaling: {
        min: MIN_NODE_SIZE,
        max: MAX_NODE_SIZE,
        label: {
          enabled: true,
          min: 20,
          max: 50,
          maxVisible: 50,
          drawThreshold: 5,
        },
      },
      mass: 2,
      color: {
        border: "#000000",
        background: "#ffffff",
      },
      font: {
        size: 20,
        align: "middle",
        color: "#000000",
      },
      shadow: {
        enabled: false,
      },
    },

    layout: {
      improvedLayout: false, // Enable improved layout algorithm
      randomSeed: 2,
      clusterThreshold: 150,
    },
    interaction: {
      hover: true,
      zoomView: true,
      navigationButtons: false,
      keyboard: false,
      tooltipDelay: 300,
      hideEdgesOnDrag: false,
      hideEdgesOnZoom: false,
      hideNodesOnDrag: false,
    },
  };
  const options2 = {
    physics: {
      enabled: true,
      solver: "forceAtlas2Based",
      forceAtlas2Based: {
        gravitationalConstant: -50,
        centralGravity: 0.01,
        springLength: 230,
        springConstant: 0.08,
        damping: 0.4,
      },
      stabilization: {
        iterations: 1000,
        updateInterval: 25,
      },
    },
    edges: {
      smooth: {
        type: "continuous",
        roundness: 0.5, // Adjust roundness for curved edges
      },
      color: {
        inherit: "from",
      },
      width: 1.5,
      length: 200, // Increase the length to prevent collapsing
      selectionWidth: 3,
      hoverWidth: 0.5,
      arrows: {
        to: { enabled: true, scaleFactor: 0.5 },
      },
    },
    layout: {
      improvedLayout: false,
    },
    interaction: {
      navigationButtons: true,
      zoomView: true,
    },
  };
  // console.log("clustersData : ", clustersData);
  return (
    <>
      {networkClusterConfig &&
      networkClusterConfig?.nodes?.length > 0 &&
      clustersData?.length > 0 ? (
        // <NetworkGraphV2
        //   nodes={networkClusterConfig?.nodes}
        //   edges={networkClusterConfig?.edges}
        //   options={options}
        //   id={id}
        //   style={style}
        //   single={single}
        //   section_index={section_index}
        //   widget_index={widget_index}
        //   metadata={metadata}
        //   PdfDownloadStatus={PdfDownloadStatus}
        //   type={type}
        //   isNetworkDeepDive={isNetworkDeepDive}
        //   clustersData={clustersData}
        // />
        <NetworkClusterV2
          nodes={networkClusterConfig?.nodes}
          edges={networkClusterConfig?.edges}
          options={options}
          id={id}
          style={style}
          single={single}
          section_index={section_index}
          widget_index={widget_index}
          metadata={metadata}
          PdfDownloadStatus={PdfDownloadStatus}
          type={type}
          isNetworkDeepDive={isNetworkDeepDive}
          clustersData={clustersData}
          chartGradientColor={chartGradientColor}
          hideSetting={hideSetting}
          onSettingChange={onSettingChange}
          dashboard={dashboard}
        />
      ) : (
        "No Data"
      )}
    </>
  );
};

export default React.memo(MultiNetowrkGraphUsingClusteringV2, isEqual);
